/**
 * THE IDEMPOTENCY KEY FOR OPENING A SITTING — one per ATTEMPT (bug, 2026-08-27).
 *
 * ── THE DEFECT ──────────────────────────────────────────────────────────────────────────────
 * `StartSessionRequest.RequestId` is the key `SqlWallet.OpenAsync` dedupes a purchase on, and the
 * contract asks for *"any stable string the client generates per attempt."* All three surfaces
 * generated one per **thread** instead — constants that never changed for the life of the pack:
 *
 *   · messaging  `${packId}:${personaId}:${startFresh}`  → `indridi::true`, forever
 *   · inhabit    `inhabit:${packId}`                     → the same string on every visit
 *   · desk       `desk:${packId}`                        → likewise
 *
 * `OpenAsync` treats a repeated key as a double-tap and returns the entitlement it already holds,
 * with `Ok = true` — right behaviour given a key that lies. So every sitting after the first reused
 * the first one's entitlement: never charged, and bound to a purchase already settled, so the bound
 * and the bell came from a spent sitting rather than the one being played.
 *
 * ── WHY A NONCE AND NOT A COUNTER ───────────────────────────────────────────────────────────
 * ⚠ NOT DERIVED FROM `generation` (the reset counter), which is the tempting fix and is wrong: it
 * restarts at 0 on every page load, so `…:1` after a reload collides with `…:1` from an earlier
 * visit and reuses a months-old entitlement — the same defect with a longer fuse. Any scheme built
 * from state that resets with the page has this hole.
 *
 * ── WHAT STILL HAS TO HOLD ──────────────────────────────────────────────────────────────────
 * The constant key was, accidentally, the double-tap protection: two opens with one key bought one
 * sitting. A nonce gives that up, so **every caller must pair it with a guard that admits one
 * attempt at a time** — `startGuard` in the thread, an in-flight ref on the surfaces whose `begin`
 * is invoked by a button. A nonce without a guard trades never-charging for charging twice, which
 * is the worse of the two errors.
 *
 * ⚠ AND A NONCE IS SPENT BY A SUCCESS, NOT BY A PRESS. An open can fail *after* the hold is taken
 * (`SessionEndpoints` takes it before the session is built, deliberately), and `SqlWallet.SweepAsync`
 * settles an orphaned hold at `Settle(0)` — the pack-start. So a caller that mints a fresh key on
 * every press charges a second pack-start for a retry of a sitting the guest never got. Callers hold
 * the key across failures, which makes the retry idempotent against the orphan, and clear it once an
 * open succeeds, which is what makes the next sitting genuinely new.
 */

/**
 * A fresh nonce for one purchase attempt.
 *
 * `crypto.randomUUID` needs a secure context — https and localhost both qualify, so it is the
 * normal path everywhere this app actually runs. The fallback covers the rest: an IP-address dev
 * host, a LAN address handed to a tester, a webview that hides the API. It is not cryptographically
 * strong and does not need to be; the requirement is only that two attempts do not collide, and
 * callers prefix it with the thread or pack it belongs to.
 *
 * ⚠ IT MUST NEVER FALL BACK TO SOMETHING STABLE — a constant here would silently restore the reuse
 * bug on exactly the hosts least likely to be tested.
 */
export function newAttemptNonce(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
      return crypto.randomUUID()
  } catch {
    /* fall through to the non-secure-context path */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}
